import type { FC, MouseEvent, ReactNode } from 'react'
import type { ReportSection } from '@/types'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { pad2 } from '@/lib/format'

interface ReportTocProps {
    sections: Array<ReportSection>
}

export const sectionAnchor = (index: number): string => `section-${pad2(index + 1)}`

/**
 * Sticky table of contents that follows the section being read.
 */
export const ReportToc: FC<ReportTocProps> = ({ sections }): ReactNode => {
    const [active, setActive] = useState(0)

    useEffect(() => {
        const elements = sections
            .map((_, index) => document.getElementById(sectionAnchor(index)))
            .filter((element): element is HTMLElement => element !== null)

        if (elements.length === 0) return

        const observer = new IntersectionObserver(entries => {
            const visible = entries.filter(entry => entry.isIntersecting)

            if (visible.length === 0) return

            const topmost = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
            const index = elements.indexOf(topmost.target as HTMLElement)

            if (index >= 0) setActive(index)
        }, { rootMargin: '-25% 0px -60% 0px' })

        elements.forEach(element => observer.observe(element))

        return () => observer.disconnect()
    }, [sections])

    const jump = (event: MouseEvent<HTMLAnchorElement>, index: number): void => {
        const target = document.getElementById(sectionAnchor(index))

        if (!target) return

        event.preventDefault()

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 120, behavior: reduced ? 'auto' : 'smooth' })
        history.replaceState(null, '', `#${sectionAnchor(index)}`)
        setActive(index)
    }

    return (
        <nav aria-label='Contents' className='desk:sticky desk:top-28'>
            <p className='hud text-mute'>
                Contents
            </p>

            <ol className='mt-4 border-t border-hairline'>
                {sections.map((section, index) => {
                    const current = index === active

                    return (
                        <li key={section.id} className='border-b border-hairline'>
                            <a
                                href={`#${sectionAnchor(index)}`}
                                onClick={event => jump(event, index)}
                                aria-current={current ? 'location' : undefined}
                                className={cn(
                                    'group flex items-baseline gap-4 py-3 transition-colors duration-300',
                                    current ? 'text-ink' : 'text-mute hover:text-ink'
                                )}
                            >
                                <span className={cn('hud shrink-0', current ? 'text-signal' : 'text-mute')}>
                                    {pad2(index + 1)}
                                </span>

                                <span className='text-[14px] leading-snug'>
                                    <span data-mdlfy-cms={`report_sections:${section.id}:heading`}>
                                        {section.heading}
                                    </span>
                                </span>
                            </a>
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}
